import Box from '@mui/material/Box';
import { Helmet } from 'react-helmet';
import { useEffect } from 'react';
import { isNullOrUndefined } from '@vy/utils';

import { useAiCopilot } from '../../../components/ai-copilot/context/use-ai-copilot';
import { VyLayout } from '../../../components/new-design/layout/VyLayout';
import { withGlobalSearchParams } from '../../../components/utility/GlobalSearchParamsBoundary';
import { withSuspenseBoundary } from '../../../components/utility/SuspenseBoundary';
import { ReportsPageContent } from './reports-page-components/ReportsPageContent';
import type { ReportType } from './reports-page-types/report-type';
import { REPORT_TYPES } from './reports-page-types/report-type';
import { useReportTypeStateParam } from './reports-page-hooks/report-type-state-param-hook';
import { ReportsMainPageV2 } from './ReportsMainPageV2';

const AR_HEALTH_SUGGESTIONS = [
  'How is my AR aging trending vs last quarter?',
  'Show me the oldest outstanding invoices',
  'Which customers are consistently paying late?',
  "What's my bad debt risk exposure?",
  'Who should I prioritize for collections today?',
];

type ReportsPageLayoutProps = {
  reportType: ReportType | null;
};

const ReportsPageLayout: React.FC<ReportsPageLayoutProps> = ({ reportType }) => {
  const { isOpen, drawerWidth } = useAiCopilot();

  const showDashboard = isNullOrUndefined(reportType) || reportType === REPORT_TYPES.ALL_REPORTS;

  return (
    <Box
      sx={{
        transition: (theme) =>
          theme.transitions.create('margin-right', {
            easing: isOpen
              ? theme.transitions.easing.easeOut
              : theme.transitions.easing.sharp,
            duration: isOpen
              ? theme.transitions.duration.enteringScreen
              : theme.transitions.duration.leavingScreen,
          }),
        marginRight: isOpen ? `${drawerWidth}px` : 0,
      }}
    >
      <VyLayout sx={{ background: '#fff' }}>
        {showDashboard ? <ReportsMainPageV2 /> : <ReportsPageContent />}
      </VyLayout>
    </Box>
  );
};

const ReportsPage: React.FC = () => {
  const [reportType] = useReportTypeStateParam();
  const isArAging = reportType === REPORT_TYPES.AR_AGING;
  const { setSuggestions } = useAiCopilot();

  useEffect(() => {
    setSuggestions(isArAging ? AR_HEALTH_SUGGESTIONS : undefined);
    return () => setSuggestions(undefined);
  }, [isArAging, setSuggestions]);

  return (
    <>
      <Helmet>
        <title>Reports</title>
      </Helmet>
      <ReportsPageLayout reportType={reportType} />
    </>
  );
};

const ReportsPageWithSuspenseBoundary = withSuspenseBoundary(withGlobalSearchParams(ReportsPage));

export { ReportsPageWithSuspenseBoundary as ReportsPage };
